import React, { useEffect, useMemo, useState } from "react";
import { Kpi } from "./Dashboard";
import { mkC } from "./lib/chartFactory";
import { api } from "./lib/api";

export default function Waste({ wst, stk, onUpload }) {
  // Build a quick map: stock-by-id and stock-by-name to compute waste % vs current stock
  const stockMap = useMemo(() => {
    const byId = {};
    const byName = {};
    (stk || []).forEach((s) => {
      if (s.id) byId[s.id] = s;
      const n = (s.name || "").toLowerCase();
      if (n) byName[n] = s;
    });
    return { byId, byName };
  }, [stk]);

  const rows = useMemo(() => {
    const wasted = (wst || []).filter((r) => (r.qty || 0) > 0);
    return wasted.map((w) => {
      const stockMatch = (w.id && stockMap.byId[w.id]) || stockMap.byName[(w.name || "").toLowerCase()] || null;
      const stockQty = stockMatch ? stockMatch.qty : 0;
      const total = (w.qty || 0) + stockQty;
      const pct = total > 0 ? +((w.qty / total) * 100).toFixed(1) : 0;
      return { ...w, stockQty, pct };
    }).sort((a, b) => b.qty - a.qty);
  }, [wst, stockMap]);

  const totalsByUnit = useMemo(() => {
    const out = {};
    rows.forEach((r) => {
      const u = r.consumption_unit || r.storage_unit || "—";
      out[u] = (out[u] || 0) + (r.qty || 0);
    });
    return out;
  }, [rows]);

  const totalWasteCount = rows.length;
  const totalWasteQty = rows.reduce((s, r) => s + (r.qty || 0), 0);

  useEffect(() => {
    if (!rows.length) return;
    const top10 = rows.slice(0, 10);
    mkC("w-top10", "bar", top10.map((r) => r.name), [{
      label: "كمية تالفة",
      data: top10.map((r) => r.qty),
      backgroundColor: "rgba(220,38,38,.7)",
      borderRadius: 4,
    }]);
  });

  if (!wst.length) {
    return (
      <div data-testid="waste-tab-empty">
        <div className="card" style={{ textAlign: "center", padding: 30 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🗑</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--brown)", marginBottom: 6 }}>ارفع تقرير «التالف» لرؤية التحليل</div>
          <p style={{ fontSize: 12.5, color: "var(--gy)", marginBottom: 14, lineHeight: 1.8 }}>
            يدعم تقرير التالف من رتم — نُظهر أعلى الأصناف هدراً، نسبتها من المخزون، والإجماليات حسب الوحدة.
          </p>
          <button data-testid="upload-waste-btn" className="btn btn-primary" onClick={onUpload}>📤 رفع ملف التالف</button>
        </div>
        <DamageArchivesSection />
      </div>
    );
  }

  return (
    <div data-testid="waste-tab">
      <Kpi
        items={[
          { l: "أصناف فيها تالف", v: totalWasteCount.toLocaleString("en-US"), c: "r" },
          { l: "إجمالي الكمية التالفة", v: totalWasteQty.toLocaleString("en-US"), c: "r" },
          { l: "أكبر صنف هدراً", v: rows[0]?.name || "—", c: "o" },
          { l: "الكمية الأعلى", v: (rows[0]?.qty || 0).toLocaleString("en-US") + " " + (rows[0]?.consumption_unit || ""), c: "o" },
        ]}
      />

      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 16 }}>📊 الإجماليات حسب الوحدة</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {Object.entries(totalsByUnit).map(([u, q]) => (
            <div key={u} style={{ background: "var(--bg)", padding: "10px 14px", borderRadius: 10, fontSize: 13 }}>
              <b style={{ color: "var(--rd)" }}>{q.toLocaleString("en-US")}</b> <span style={{ color: "var(--gy)" }}>{u}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>🔝 أعلى 10 أصناف تالفة</h3>
        <div className="ch" style={{ height: "clamp(280px,40vh,420px)" }}>
          <canvas id="w-top10" />
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>📋 قائمة التالف الكاملة ({rows.length})</h3>
        <div style={{ overflowX: "auto" }}>
          <table className="t" data-testid="waste-table">
            <thead>
              <tr>
                <th>#</th>
                <th>الصنف</th>
                <th>كمية تالفة</th>
                <th>الوحدة</th>
                <th>المتوفّر حالياً</th>
                <th>نسبة التالف</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || r.name}>
                  <td>{i + 1}</td>
                  <td><b>{r.name}</b><div style={{ fontSize: 10.5, color: "var(--gy)" }}>{r.id}</div></td>
                  <td><b style={{ color: "var(--rd)" }}>{r.qty.toLocaleString("en-US")}</b></td>
                  <td>{r.consumption_unit || r.storage_unit || "—"}</td>
                  <td>{(r.stockQty || 0).toLocaleString("en-US")}</td>
                  <td>
                    <span className={`stk-pill ${r.pct > 30 ? "stk-out" : r.pct > 15 ? "stk-critical" : r.pct > 5 ? "stk-low" : "stk-safe"}`}>
                      {r.pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Damage archives — exported & approved damage bundles ready for the
          accounting office. Rendered only when at least one archive exists. */}
      <DamageArchivesSection />
    </div>
  );
}

function DamageArchivesSection() {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({}); // {archiveId: bool}
  const [me, setMe] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/damage-archives", { params: { limit: 100 } });
      setArchives(data.items || []);
    } catch (_e) { /* user may lack damage.view_all — silently hide section */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let cancelled = false;
    load();
    // Cheap role probe — used to gate the admin delete button below.
    api.get("/auth/me").then(({ data }) => { if (!cancelled) setMe(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const deleteArchive = async (aid, title, count) => {
    const warn = `⚠️ حذف نهائي — هذا الإجراء غير قابل للتراجع.\n\n` +
                 `سيُحذف الأرشيف «${title}» وكل بنوده (${count} طلب) من النظام، ` +
                 `بما في ذلك الصور وملفات الـ Excel/PDF.`;
    if (!window.confirm(warn)) return;
    const typed = window.prompt(`اكتب «حذف الأرشيف» للتأكيد النهائي:`);
    if (typed !== "حذف الأرشيف") return;
    try {
      await api.delete(`/damage-archives/${aid}`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || e?.message || "خطأ"); }
  };

  if (loading) return null;
  if (archives.length === 0) return null;

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="card" data-testid="damage-archives-section"
      style={{ marginTop: 16, border: "2px solid #7C3AED", background: "linear-gradient(135deg, #fff 0%, #F5F3FF 100%)" }}>
      <h3 style={{ margin: "0 0 12px", fontSize: 16, color: "#5B21B6" }}>
        📦 ملفات الإتلاف المعتمدة ({archives.length})
      </h3>
      <div style={{ fontSize: 12, color: "var(--gy)", marginBottom: 12, lineHeight: 1.7 }}>
        ملفات Excel و PDF نهائية مع ختم الاعتماد وصور المنتجات — جاهزة للإرسال للمكتب المحاسبي.
      </div>
      {archives.map((a) => (
        <div key={a.id} className="card" data-testid={`archive-${a.id}`}
          style={{ padding: 12, marginBottom: 8, borderInlineStart: "4px solid #7C3AED" }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 250 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--brown-deep, #5B21B6)" }}>
                {a.title}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--gy)", marginTop: 4 }}>
                {a.item_count} طلب · إجمالي الكمية: {(a.total_quantity || 0).toLocaleString("en-US")} ·
                اعتمده: {a.approver_name} ·
                {a.created_at?.slice(0, 16).replace("T", " ")}
              </div>
              <div style={{ fontSize: 11, color: "var(--gy)", marginTop: 3 }}>
                الفروع: {(a.branches || []).join("، ") || "—"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {a.has_xlsx && (
                <a href={`${BACKEND}/api/damage-archives/${a.id}/download.xlsx`}
                  target="_blank" rel="noreferrer" data-testid={`archive-xlsx-${a.id}`}
                  style={btnArc("#0891B2")}>📊 Excel</a>
              )}
              {a.has_pdf && (
                <a href={`${BACKEND}/api/damage-archives/${a.id}/download.pdf`}
                  target="_blank" rel="noreferrer" data-testid={`archive-pdf-${a.id}`}
                  style={btnArc("#DC2626")}>📄 PDF</a>
              )}
              {me?.role === "admin" && (
                <button onClick={() => deleteArchive(a.id, a.title, a.item_count)}
                  data-testid={`archive-admin-del-${a.id}`}
                  style={btnArc("#7F1D1D")}
                  title="حذف الأرشيف كاملاً مع كل بنوده (أدمن فقط)">
                  🗑 حذف
                </button>
              )}
              <button onClick={() => setOpen({ ...open, [a.id]: !open[a.id] })}
                data-testid={`archive-toggle-${a.id}`} style={btnArc("#6B7280")}>
                {open[a.id] ? "▲" : "▼"}
              </button>
            </div>
          </div>
          {open[a.id] && <ArchiveDetails archiveId={a.id} />}
        </div>
      ))}
    </div>
  );
}

function ArchiveDetails({ archiveId }) {
  const [details, setDetails] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/damage-archives/${archiveId}`);
        if (!cancelled) setDetails(data);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [archiveId]);
  if (!details) return <div style={{ fontSize: 12, color: "var(--gy)", padding: 6 }}>جارٍ تحميل التفاصيل...</div>;
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border, #eee)" }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>تفاصيل البنود ({details.items?.length || 0})</div>
      <table className="t" style={{ fontSize: 11.5 }}>
        <thead>
          <tr>
            <th>#</th><th>المنتج</th><th>الكمية</th><th>الوحدة</th><th>السبب</th><th>الفرع</th><th>التاريخ</th>
          </tr>
        </thead>
        <tbody>
          {(details.items || []).map((it, i) => (
            <tr key={it.id}>
              <td>{i + 1}</td>
              <td>{it.product_name}</td>
              <td>{it.quantity}</td>
              <td>{it.product_unit}</td>
              <td>{it.reason}</td>
              <td>{it.branch || "—"}</td>
              <td>{(it.submitted_at || "").slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const btnArc = (bg) => ({
  padding: "5px 12px", background: bg, color: "#fff",
  textDecoration: "none", border: "none", borderRadius: 4,
  fontSize: 12, fontWeight: 700, cursor: "pointer",
});
